(function () {
  'use strict';

  // Feriados nacionais do Brasil (dia/mês fixos, recorrentes todo ano)
  const HOLIDAYS = [
    { name: 'Ano Novo', day: 1, month: 1 },
    { name: 'Tiradentes', day: 21, month: 4 },
    { name: 'Dia do Trabalho', day: 1, month: 5 },
    { name: 'Independência do Brasil', day: 7, month: 9 },
    { name: 'Nossa Senhora Aparecida', day: 12, month: 10 },
    { name: 'Finados', day: 2, month: 11 },
    { name: 'Proclamação da República', day: 15, month: 11 },
    { name: 'Natal', day: 25, month: 12 },
  ];

  class ChatCTA {
    constructor(rootEl) {
      this.root = rootEl;
      this.nudge = rootEl.querySelector('#chat-cta-nudge');
      this.msgEl = rootEl.querySelector('#chat-cta-msg');
      this.fab = rootEl.querySelector('#chat-cta-fab');
      this.dot = rootEl.querySelector('#chat-cta-dot');
      this.dismiss = rootEl.querySelector('#chat-cta-dismiss');

      this.config = this._readConfig();
      this.msgIdx = 0;
      this.dismissed = false;
      this.suppressed = false;
      this.timers = [];
      this._scrollHandler = null;
      this._revealed = false;

      this._init();
    }

    _readConfig() {
      const d = this.root.dataset;
      const parseTime = (t = '00:00') => {
        const [h = 0, m = 0] = t.split(':').map(Number);
        return h + m / 60;
      };
      // Distinto de "|| default": 0 é um valor válido e deliberado aqui
      // (limite 0 = balão desligado; espera 0 = sem cooldown), não deve
      // cair no fallback como aconteceria com `parseInt(...) || default`.
      const parseIntOrDefault = (value, fallback) => {
        const parsed = parseInt(value, 10);
        return Number.isNaN(parsed) ? fallback : parsed;
      };
      const msgs = [d.message1, d.message2, d.message3].filter(Boolean);
      return {
        openHour: parseTime(d.openTime),
        closeHour: parseTime(d.closeTime),
        activeDays:
          d.activeDays !== undefined
            ? d.activeDays
                .split(',')
                .filter(s => s !== '')
                .map(Number)
                .filter(n => !isNaN(n) && n >= 0)
            : [0, 1, 2, 3, 4, 5, 6],
        messages: msgs.length ? msgs : ['Oi! Posso te ajudar? ;;)'],
        firstDelay: parseInt(d.nudgeFirstDelay, 10) || 1600,
        delayPdp: parseInt(d.nudgeDelayPdp, 10) || 0,
        delayPlp: parseInt(d.nudgeDelayPlp, 10) || 0,
        scrollThreshold: parseInt(d.nudgeScrollThreshold, 10) || 0,
        visible: parseInt(d.nudgeVisible, 10) || 5200,
        gap: parseInt(d.nudgeGap, 10) || 9000,
        hideOnCart: d.hideOnCart === 'true',
        hideOnCheckout: d.hideOnCheckout === 'true',
        whatsappUrl: d.whatsappUrl || '',
        nudgeEnabled: d.nudgeEnabled !== 'false',
        maxShowsPerSession: parseIntOrDefault(d.nudgeMaxShowsPerSession, 3),
        dismissCooldownHours: parseIntOrDefault(
          d.nudgeDismissCooldownHours,
          24
        ),
      };
    }

    // ── Page type ─────────────────────────────────────────────────────────────

    _detectPageType() {
      const path = window.location.pathname;
      if (path.includes('/products/')) return 'pdp';
      if (path.includes('/collections/')) return 'plp';
      return 'other';
    }

    _getFirstDelay() {
      const type = this._detectPageType();
      if (type === 'pdp' && this.config.delayPdp > 0)
        return this.config.delayPdp;
      if (type === 'plp' && this.config.delayPlp > 0)
        return this.config.delayPlp;
      return this.config.firstDelay;
    }

    // ── Suppression ───────────────────────────────────────────────────────────

    _SUPPRESS_TTL = 60 * 60 * 1000; // 1 hora em ms

    _isRecentlySuppressed() {
      const suppressedAt = window.ZrzsSideMenuStorage?.getCartSuppressedAt();
      if (!suppressedAt) return false;
      return Date.now() - suppressedAt < this._SUPPRESS_TTL;
    }

    _writeSuppressedFlag() {
      window.ZrzsSideMenuStorage?.setCartSuppressedAt(Date.now());
    }

    // Fechamento manual do balão (botão X): fica fora por
    // `dismissCooldownHours`, mesmo recarregando a página ou voltando numa
    // sessão futura — diferente da supressão por carrinho acima, que tem
    // TTL fixo de 1h e outro gatilho.
    _isDismissCooldownActive() {
      const { dismissCooldownHours } = this.config;
      if (dismissCooldownHours <= 0) return false;

      const dismissedAt = window.ZrzsSideMenuStorage?.getDismissedAt();
      if (!dismissedAt) return false;

      return Date.now() - dismissedAt < dismissCooldownHours * 60 * 60 * 1000;
    }

    // Limite de exibições por sessão (sessionStorage — ver
    // zrzs-side-menu-storage.js): persiste ao navegar entre páginas na
    // mesma aba e zera sozinho quando a aba/janela é fechada, que é
    // exatamente a semântica de "sessão" que essa regra precisa.
    _hasReachedSessionShowLimit() {
      const { maxShowsPerSession } = this.config;
      // 0 = sem limite (não confundir com desligar o balão — isso já é
      // papel do toggle "Popup de chat ativo"/nudgeEnabled).
      if (maxShowsPerSession <= 0) return false;
      const shown = window.ZrzsSideMenuStorage?.getNudgeShowCount() ?? 0;
      return shown >= maxShowsPerSession;
    }

    async _checkSuppressed() {
      const { hideOnCart, hideOnCheckout } = this.config;
      if (!hideOnCart && !hideOnCheckout) return false;

      if (hideOnCheckout && this._isRecentlySuppressed()) return true;

      try {
        const cart = await fetch('/cart.js').then(r => r.json());
        if (cart.item_count > 0 && hideOnCart) return true;
      } catch {}

      return false;
    }

    _listenCartUpdates() {
      if (!this.config.hideOnCheckout) return;

      const suppress = () => {
        this._writeSuppressedFlag();
        this.suppressed = true;
        this._stopCycle();
      };

      // Capture phase garante disparo antes de qualquer outro handler
      // Cobre o botão da PDP e o botão de cart dentro do modal de lentes
      document.addEventListener(
        'click',
        e => {
          const el = /** @type {Element} */ (e.target);
          if (
            el.closest('.lens-selection__button') ||
            el.closest('.lens-selection__cart-button')
          ) {
            suppress();
          }
        },
        true
      );
    }

    // ── Online state ──────────────────────────────────────────────────────────

    _isHoliday(now) {
      const day = now.getDate();
      const month = now.getMonth() + 1; // getMonth() é 0-indexado
      return HOLIDAYS.some(h => h.day === day && h.month === month);
    }

    // Usa o <zrzs-business-hours> compartilhado; cálculo abaixo é fallback
    // caso o componente ainda não tenha sido definido (ordem de scripts).
    _isOnline() {
      const shared = /** @type {any} */ (
        document.querySelector('zrzs-business-hours')
      );
      if (shared && typeof shared.isOpenNow === 'function') {
        return shared.isOpenNow();
      }

      const now = new Date();
      if (this._isHoliday(now)) return false;

      const day = now.getDay(); // 0=Dom, 1=Seg, ..., 6=Sáb
      const h = now.getHours() + now.getMinutes() / 60;
      return (
        this.config.activeDays.includes(day) &&
        h >= this.config.openHour &&
        h < this.config.closeHour
      );
    }

    _applyOnlineState() {
      const online = this._isOnline();
      this.root.dataset.online = online ? 'true' : 'false';
      if (this.dot) this.dot.style.display = online ? '' : 'none';
      return online;
    }

    // ── Scroll trigger ────────────────────────────────────────────────────────

    _setupScrollTrigger(cycle) {
      if (!this.config.scrollThreshold) return;

      this._scrollHandler = () => {
        const total =
          document.documentElement.scrollHeight - window.innerHeight;
        if (total <= 0) return;
        const pct = (window.scrollY / total) * 100;
        if (pct >= this.config.scrollThreshold) {
          this._removeScrollListener();
          // Cancel the pending firstDelay timer and show nudge immediately
          this.timers.forEach(clearTimeout);
          this.timers = [];
          cycle();
        }
      };

      window.addEventListener('scroll', this._scrollHandler, { passive: true });
    }

    _removeScrollListener() {
      if (this._scrollHandler) {
        window.removeEventListener('scroll', this._scrollHandler);
        this._scrollHandler = null;
      }
    }

    // ── Nudge cycle ───────────────────────────────────────────────────────────

    _startCycle() {
      this.timers.forEach(clearTimeout);
      this.timers = [];
      this._removeScrollListener();
      this.nudge?.classList.remove('show');

      const online = this._applyOnlineState();

      if (
        !this.config.nudgeEnabled ||
        !online ||
        this.dismissed ||
        this.suppressed ||
        this._isDismissCooldownActive() ||
        this._hasReachedSessionShowLimit()
      )
        return;

      const _isSideMenuOpen = () => {
        const sideMenu = this.root.closest('side-menu');
        return (
          sideMenu &&
          typeof sideMenu.isMenuOpen === 'function' &&
          sideMenu.isMenuOpen()
        );
      };

      const cycle = () => {
        this._removeScrollListener();
        if (_isSideMenuOpen()) {
          this.timers.push(setTimeout(cycle, this.config.gap));
          return;
        }
        // Reavalia o limite a cada exibição: ele pode ser atingido no meio
        // do ciclo (não só na checagem inicial acima).
        if (this._hasReachedSessionShowLimit()) {
          this._stopCycle();
          return;
        }
        window.ZrzsSideMenuStorage?.incrementNudgeShowCount();
        this.nudge?.classList.add('show');
        if (this.msgEl)
          this.msgEl.textContent = this.config.messages[this.msgIdx];

        this.timers.push(
          setTimeout(() => {
            this.nudge?.classList.remove('show');
            this.timers.push(
              setTimeout(() => {
                this.msgIdx = (this.msgIdx + 1) % this.config.messages.length;
                cycle();
              }, this.config.gap)
            );
          }, this.config.visible)
        );
      };

      this._setupScrollTrigger(cycle);
      this.timers.push(setTimeout(cycle, this._getFirstDelay()));
    }

    _stopCycle() {
      this.timers.forEach(clearTimeout);
      this.timers = [];
      this._removeScrollListener();
      this.nudge?.classList.remove('show');
    }

    // ── Actions ───────────────────────────────────────────────────────────────

    _dismissNudge(e) {
      e?.stopPropagation();
      this.dismissed = true;
      window.ZrzsSideMenuStorage?.setDismissedAt(Date.now());
      this._stopCycle();
    }

    // Nudge click → redirect to WhatsApp (compre conosco)
    _openWhatsapp() {
      if (this.config.whatsappUrl) {
        window.open(this.config.whatsappUrl, '_blank', 'noopener,noreferrer');
      }
    }

    // FAB click → open side menu
    _openMenu() {
      this._stopCycle();
      const sideMenu = this.root.closest('side-menu');
      if (sideMenu && typeof sideMenu.openWindowSideMenu === 'function') {
        sideMenu.openWindowSideMenu();
      }
    }

    _bind() {
      this.fab?.addEventListener('click', () => this._openMenu());
      this.nudge?.addEventListener('click', () => this._openWhatsapp());
      this.dismiss?.addEventListener('click', e => this._dismissNudge(e));
    }

    // ── Reveal ────────────────────────────────────────────────────────────────

    // Invisível (CSS) até inicializar, pra evitar o "pulo" do cálculo de
    // posição do side-menu.js; fallback garante que apareça mesmo se algo travar.
    _reveal() {
      if (this._revealed) return;
      this._revealed = true;
      requestAnimationFrame(() => this.root.classList.add('chat-cta--visible'));
    }

    // ── Init ──────────────────────────────────────────────────────────────────

    async _init() {
      setTimeout(() => this._reveal(), 4000);

      this.suppressed = await this._checkSuppressed();
      this._bind();
      this._listenCartUpdates();
      this._startCycle();
      this._reveal();

      // Re-evaluate online state every minute
      setInterval(() => this._startCycle(), 60_000);
    }

    destroy() {
      this._stopCycle();
    }
  }

  function init() {
    const root = document.getElementById('chat-cta-root');
    if (root && !root._chatCTA) {
      root._chatCTA = new ChatCTA(root);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
